import { type Editor } from '@tiptap/react';
import { memo, type ReactNode, useCallback, useState } from 'react';

import { PrivateWorkspaceAgentPanelSelectionDropOverlay } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelSelectionDropOverlay';
import { usePrivateWorkspaceAgentComposerSelectionDrop } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentComposerSelectionDrop';

export type PrivateWorkspaceAgentPanelChatDropZoneRenderProps = {
  isSuppressingEditorAgentDrag: boolean;
  onComposerEditorChange: (editor: Editor | null) => void;
};

export type PrivateWorkspaceAgentPanelChatDropZoneProps = {
  children: (props: PrivateWorkspaceAgentPanelChatDropZoneRenderProps) => ReactNode;
  isComposerDisplayed: boolean;
  isComposerEnabled: boolean;
};

export const PrivateWorkspaceAgentPanelChatDropZone = memo(
  ({ children, isComposerDisplayed, isComposerEnabled }: PrivateWorkspaceAgentPanelChatDropZoneProps) => {
    const [composerEditor, setComposerEditor] = useState<Editor | null>(null);

    const {
      handleAgentSelectionDragOverCapture,
      handleAgentSelectionDragLeave,
      handleDropCapture,
      isDragOver,
      isSuppressingEditorAgentDrag,
      selectionDropOverlayDescription,
    } = usePrivateWorkspaceAgentComposerSelectionDrop({
      editor: composerEditor,
      isComposerDisplayed,
      isComposerEnabled,
    });

    const handleComposerEditorChange = useCallback((editor: Editor | null) => {
      setComposerEditor(editor);
    }, []);

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: HTML5 drop target on the chat pane; thread and composer keep their own interaction semantics.
      <div
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="WORKSPACE_AGENT_PANEL_CHAT_DROP_ZONE"
        onDragEnterCapture={handleAgentSelectionDragOverCapture}
        onDragLeave={handleAgentSelectionDragLeave}
        onDragOverCapture={handleAgentSelectionDragOverCapture}
        onDropCapture={handleDropCapture}
      >
        {children({ isSuppressingEditorAgentDrag, onComposerEditorChange: handleComposerEditorChange })}
        <PrivateWorkspaceAgentPanelSelectionDropOverlay
          description={selectionDropOverlayDescription}
          isDragOver={isDragOver}
        />
      </div>
    );
  },
);

PrivateWorkspaceAgentPanelChatDropZone.displayName = 'PrivateWorkspaceAgentPanelChatDropZone';
